import api from 'store/api';
import { setErrorCatch } from 'store/status/actions';
import {
  ScheduleRange,
  ScheduleRangesData,
  ScheduleRangeFilters,
  ScheduleRangePostMutationRequest,
  ScheduleRangePatchMutationRequest,
} from './interfaces';

export const scheduleRangesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScheduleRanges: builder.query<
      ScheduleRangesData,
      Partial<ScheduleRangeFilters>
    >({
      query: (filters) => ({
        url: '/schedule-ranges',
        params: filters,
      }),
      providesTags: ['ScheduleRanges'],
    }),
    getScheduleRange: builder.query<ScheduleRange, string>({
      query: (id) => `/schedule-ranges/${id}`,
      providesTags: ['ScheduleRanges'],
    }),
    addScheduleRange: builder.mutation<
      ScheduleRange,
      ScheduleRangePostMutationRequest
    >({
      query: (body) => ({
        url: '/schedule-ranges',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ScheduleRanges'],
    }),
    updateScheduleRange: builder.mutation<
      ScheduleRange,
      ScheduleRangePatchMutationRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/schedule-ranges/${id}`,
        headers: { 'content-type': 'application/merge-patch+json' },
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
      invalidatesTags: ['ScheduleRanges'],
    }),
    deleteScheduleRange: builder.mutation<void, string>({
      query: (id) => ({
        url: `/schedule-ranges/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { getState, dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const scheduleRangesCache = api.util.selectInvalidatedBy(getState(), [
            { type: 'ScheduleRanges' },
          ]);

          scheduleRangesCache.forEach(({ endpointName, originalArgs }) => {
            if (endpointName === 'getScheduleRange' && originalArgs === id) {
              dispatch(
                scheduleRangesApi.util.updateQueryData(
                  'getScheduleRanges',
                  originalArgs,
                  (draft) => ({
                    ...draft,
                    'hydra:member': draft?.['hydra:member'].filter(
                      (item) => item.id !== id,
                    ),
                  }),
                ),
              );
            }
          });
        } catch (error) {
          setErrorCatch(error);
        }
      },
    }),
  }),
});

export const {
  useGetScheduleRangesQuery,
  useGetScheduleRangeQuery,
  useAddScheduleRangeMutation,
  useUpdateScheduleRangeMutation,
  useDeleteScheduleRangeMutation,
} = scheduleRangesApi;
